import React, { useRef, useState, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { sentenceCase } from "change-case";
// material
import {
  Card,
  Skeleton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TablePagination,
  TableRow,
  Typography,
  CardContent,
  Box,
} from "@material-ui/core";
import { makeStyles } from "@material-ui/styles";
import * as locales from "@material-ui/core/locale";
import { createTheme, ThemeProvider } from "@material-ui/core/styles";
// components
import Scrollbar from "../Scrollbar";
import { TareasListHead } from "../_dashboard/tareas";
import { Alerta } from "../Alerta";
import { useSelector } from "react-redux";
import { tareasApi } from "src/services/tareasApi";
import moment from "moment";

// Sistema de filtros
import useTareasFilters from "src/components/tareas/hooks/useTareasFilters";
import TareasFilterPanel from "src/components/tareas/filters/TareasFilterPanel";

// Estilos de estados (importar desde el archivo de estilos)
import { ESTADO_COLORS } from "src/components/tareas/styles/tareasStyles.js";

// ----------------------------------------------------------------------

const styles = makeStyles((theme) => ({
  root: {
    minWidth: 275,
  },
  title: {
    textAlign: "center",
    fontSize: "24px",
    marginTop: 20,
  },
  pos: {
    marginBottom: 15,
  },
}));

export default function TareasEmpleado(props) {
  const { value, empleado } = props;
  const navigate = useNavigate();
  const classes = styles();
  const isMounted = useRef(true);

  // Estados de paginación y ordenamiento
  const [page, setPage] = useState(0);
  const [order, setOrder] = useState("asc");
  const [selected, setSelected] = useState([]);
  const [orderBy, setOrderBy] = useState("fechaInicio");
  const [rowsPerPage, setRowsPerPage] = useState(() => {
    const saved = localStorage.getItem("rowPerPageTaskEmpleado");
    return saved ? parseInt(saved, 10) : 10;
  });
  const [locale] = useState("esES");

  // Estados de alertas
  // eslint-disable-next-line no-unused-vars
  const [error, setError] = useState("");
  const [showError, setShowError] = useState(false);
  const [showAlert, setShowAlert] = useState(false);

  // Redux state
  const { sucursal: sucursalObtenida, rol: rolObtenido } = useSelector(
    (state) => state.auth.user,
  );

  // ==================== FILTROS ====================
  const {
    filtros,
    filtrosActivos,
    hayFiltrosActivos,
    updateFiltro,
    toggleEstado,
    limpiarFiltros,
    removerFiltro,
  } = useTareasFilters([], {
    rolObtenido,
    storageKey: "tareasEmpleadoFilters",
  });

  // Determinar qué estados mostrar
  const estadosParaQuery = useMemo(() => {
    if (filtros.estados && filtros.estados.length > 0) {
      return filtros.estados.join(",");
    }
    // Si no hay filtros, mostrar todas las tareas
    return "";
  }, [filtros.estados]);

  const {
    data: tareasResponse,
    isLoading: loadingTable,
    isFetching,
  } = tareasApi.useGetTareasAllQuery(
    {
      id: sucursalObtenida.refSucursal,
      refEmpleado: empleado.rut,
      page: page,
      limit: rowsPerPage,
      orderBy: orderBy,
      order: order,
      busqueda: filtros.busqueda,
      estados: estadosParaQuery,
      tipoTarea: filtros.tipoTarea,
      cliente: filtros.cliente?.value || null,
      incluirAdicionales: false,
    },
    {
      skip: value !== 2,
    },
  );

  const tareasData = useMemo(
    () => tareasResponse?.data ?? [],
    [tareasResponse?.data],
  );

  const paginationMeta = tareasResponse?.pagination || {
    total: 0,
    page: 0,
    limit: 10,
    totalPages: 0,
  };

  const opcionesClientes = useMemo(() => {
    const clientesMap = new Map();
    tareasData.forEach((tarea) => {
      if (tarea.refCliente && tarea.nombreCliente) {
        clientesMap.set(tarea.refCliente, {
          value: tarea.refCliente,
          label: tarea.nombreCliente,
        });
      }
    });
    return Array.from(clientesMap.values()).sort((a, b) =>
      a.label.localeCompare(b.label),
    );
  }, [tareasData]);

  // Tabla header (agregamos columna Estado)
  const tableHead = useState([
    { id: "nombreCliente", label: "Nombre Cliente", alignRight: false },
    { id: "nombre", label: "Tarea", alignRight: false },
    { id: "tipoTarea", label: "Tipo Tarea", alignRight: false },
    { id: "estado", label: "Estado", alignRight: false },
    { id: "fechaInicio", label: "Fecha Inicio", alignRight: false },
    { id: "fechaTermino", label: "Fecha Término", alignRight: false },
    { id: "diasTranscurridos", label: "Días Transcurridos", alignRight: false },
  ])[0];

  // Effects
  useEffect(() => {
    return () => {
      isMounted.current = false;
    };
  }, []);

  // Reset página cuando cambian los filtros
  useEffect(() => {
    setPage(0);
  }, [filtros]);

  // Handlers
  const handleRequestSort = (event, property) => {
    const isAsc = orderBy === property && order === "asc";
    setOrder(isAsc ? "desc" : "asc");
    setOrderBy(property);
    setPage(0);
  };

  const handleSelectAllClick = (event) => {
    if (event.target.checked) {
      const newSelecteds = tareasData.map((n) => n.id);
      setSelected(newSelecteds);
      return;
    }
    setSelected([]);
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    const value = parseInt(event.target.value, 10);
    localStorage.setItem("rowPerPageTaskEmpleado", value);
    setRowsPerPage(value);
    setPage(0);
  };

  // Verificar si no hay elementos encontrados
  const isElementNotFound = tareasData.length === 0 && hayFiltrosActivos;

  // Renderizar chip de estado
  const renderEstadoLabel = (estado) => {
    const colors =
      ESTADO_COLORS[estado] || ESTADO_COLORS["CANCELADO O RECHAZADO"];

    return (
      <Box
        sx={{
          display: "inline-flex",
          alignItems: "center",
          padding: "4px 12px",
          borderRadius: "14px",
          backgroundColor: colors.bg,
        }}
      >
        <Box
          component="span"
          sx={{
            width: 8,
            height: 8,
            borderRadius: "50%",
            backgroundColor: colors.dot,
            marginRight: "6px",
          }}
        />
        <Typography
          variant="caption"
          sx={{
            color: colors.text,
            fontWeight: 500,
            fontSize: "0.75rem",
          }}
        >
          {sentenceCase(estado)}
        </Typography>
      </Box>
    );
  };

  // Renderizado de filas
  const renderTableBody = () => {
    return tareasData.map((row) => {
      const {
        id,
        nombre,
        tipoTarea,
        nombreCliente,
        estado,
        fechaInicio,
        fechaTermino,
        diasTranscurridos,
      } = row;
      const isItemSelected = selected.indexOf(id) !== -1;

      return (
        <TableRow
          hover
          key={id}
          tabIndex={-1}
          role="checkbox"
          selected={isItemSelected}
          aria-checked={isItemSelected}
        >
          <TableCell
            onClick={() => navigate("/dashboard/tareas/" + id)}
            style={{ cursor: "pointer" }}
            align="left"
          >
            {nombreCliente}
          </TableCell>
          <TableCell
            align="left"
            onClick={() => navigate("/dashboard/tareas/" + id)}
            style={{ cursor: "pointer" }}
          >
            {nombre}
          </TableCell>
          <TableCell
            align="left"
            onClick={() => navigate("/dashboard/tareas/" + id)}
            style={{ cursor: "pointer" }}
          >
            {tipoTarea}
          </TableCell>
          <TableCell align="left">{renderEstadoLabel(estado)}</TableCell>
          <TableCell
            align="left"
            onClick={() => navigate("/dashboard/tareas/" + id)}
            style={{ cursor: "pointer" }}
          >
            {fechaInicio
              ? moment(fechaInicio, "YYYY/MM/DD").format("DD/MM/YYYY")
              : "—"}
          </TableCell>
          <TableCell
            align="left"
            onClick={() => navigate("/dashboard/tareas/" + id)}
            style={{ cursor: "pointer" }}
          >
            {fechaTermino
              ? moment(fechaTermino, "YYYY/MM/DD").format("DD/MM/YYYY")
              : "—"}
          </TableCell>
          <TableCell
            align="left"
            onClick={() => navigate("/dashboard/tareas/" + id)}
            style={{ cursor: "pointer" }}
          >
            {diasTranscurridos === 1
              ? `${diasTranscurridos} día`
              : `${diasTranscurridos} días`}
          </TableCell>
        </TableRow>
      );
    });
  };

  return (
    <div className={classes.pos}>
      {value === 2 && (
        <Card className={classes.root} variant="outlined">
          <Typography variant="h5" component="h2" className={classes.title}>
            Tareas del Empleado
          </Typography>
          <CardContent>
            {/* Panel de filtros */}
            <TareasFilterPanel
              filtros={filtros}
              updateFiltro={updateFiltro}
              toggleEstado={toggleEstado}
              limpiarFiltros={limpiarFiltros}
              removerFiltro={removerFiltro}
              filtrosActivos={filtrosActivos}
              hayFiltrosActivos={hayFiltrosActivos}
              opcionesClientes={opcionesClientes}
              opcionesEmpleados={[]} // No mostramos filtro de empleado aquí
              isAdmin={false} // Ocultamos filtro de empleado
            />

            <Scrollbar>
              <TableContainer sx={{ minWidth: 800 }}>
                {loadingTable || isFetching ? (
                  <Skeleton variant="rectangular" width="100%" height="500px" />
                ) : (
                  <Table>
                    <TareasListHead
                      order={order}
                      orderBy={orderBy}
                      headLabel={tableHead}
                      rowCount={paginationMeta.total}
                      onRequestSort={handleRequestSort}
                      onSelectAllClick={handleSelectAllClick}
                    />
                    <TableBody>
                      {renderTableBody()}

                      {/* No se encontraron resultados */}
                      {isElementNotFound && (
                        <TableRow>
                          <TableCell align="center" colSpan={7} sx={{ py: 3 }}>
                            <Box>
                              <Typography variant="body1" gutterBottom>
                                No se encontraron tareas con los filtros
                                aplicados
                              </Typography>
                              <Typography
                                variant="body2"
                                color="textSecondary"
                                component="span"
                                style={{
                                  cursor: "pointer",
                                  textDecoration: "underline",
                                }}
                                onClick={limpiarFiltros}
                              >
                                Limpiar filtros
                              </Typography>
                            </Box>
                          </TableCell>
                        </TableRow>
                      )}

                      {/* No hay tareas */}
                      {!hayFiltrosActivos && tareasData.length === 0 && (
                        <TableRow>
                          <TableCell align="center" colSpan={7} sx={{ py: 3 }}>
                            No hay tareas que mostrar.
                          </TableCell>
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>
                )}
              </TableContainer>
            </Scrollbar>

            <ThemeProvider
              theme={(outerTheme) => createTheme(outerTheme, locales[locale])}
            >
              <TablePagination
                rowsPerPageOptions={[10, 25, 50]}
                component="div"
                count={paginationMeta.total}
                rowsPerPage={rowsPerPage}
                page={page}
                onPageChange={handleChangePage}
                onRowsPerPageChange={handleChangeRowsPerPage}
              />
            </ThemeProvider>
          </CardContent>

          <Alerta
            showAlert={showError}
            setShowAlert={setShowError}
            message={error}
            color="error"
          />

          {showAlert && (
            <Alerta
              showAlert={showAlert}
              setShowAlert={setShowAlert}
              message={error}
              color={"success"}
            />
          )}
        </Card>
      )}
    </div>
  );
}
